import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import {
  buildDashboardDataTableColumns,
  DashboardDataTableColumnKind,
} from '@shigo-ui/dashboard-ui';
import { SegmentedControl } from '@shigo-ui/ui';
import { Sparkline } from '@shigo-ui/viz';

import { dashboardT } from '../../../../../lib/messages';
import { dashboardReportExplorerSearch } from '../../../dashboardReporting';
import { formatCurrency, formatNumber } from '../../../../../lib/format';
import type {
  DashboardDateRange,
  DashboardSrpmRankingItem,
  DashboardWidget,
  DashboardWidgetType,
} from '../../../model';
import { dashboardTrendTone } from '../../../trend';
import { DashboardComparisonToggle } from '@/components/dashboard/DashboardComparisonToggle';
import { DashboardWidgetTable } from '../../DashboardWidgetTable';
import { dashboardTruncatedNameClassName, DashboardTruncatedName } from '../DashboardTruncatedName';

interface DashboardSrpmRankingTableProps {
  widget: Extract<DashboardWidget, { type: DashboardWidgetType.SrpmRanking }>;
  topItems: DashboardSrpmRankingItem[];
  bottomItems: DashboardSrpmRankingItem[];
  loading: boolean;
  errorMessage?: ReactNode | undefined;
  range: DashboardDateRange;
}

interface DashboardSrpmRankingRow extends DashboardSrpmRankingItem {
  rank: number;
}

enum DashboardSrpmRankingMode {
  Top = 'TOP',
  Bottom = 'BOTTOM',
}

const srpmRankingOptions = [
  {
    value: DashboardSrpmRankingMode.Top,
    label: dashboardT('dashboard.srpmRanking.top'),
  },
  {
    value: DashboardSrpmRankingMode.Bottom,
    label: dashboardT('dashboard.srpmRanking.bottom'),
  },
] as const;

const minimumTrendPoints = 2;

export function DashboardSrpmRankingTable({
  widget,
  topItems,
  bottomItems,
  loading,
  errorMessage,
  range,
}: DashboardSrpmRankingTableProps) {
  const [mode, setMode] = useState(DashboardSrpmRankingMode.Top);
  const [showComparison, setShowComparison] = useState(false);
  const items = mode === DashboardSrpmRankingMode.Top ? topItems : bottomItems;
  const rankedItems = items.map((item, index) => ({ ...item, rank: index + 1 }));
  const trendWindowEndDate = items[0]?.srpmTrend7d.at(-1)?.date ?? range.dateTo;

  return (
    <DashboardWidgetTable
      title={dashboardT('dashboard.srpmRanking.title')}
      searchPlaceholder={dashboardT('dashboard.integrations.search')}
      items={rankedItems}
      columns={rankingColumns(showComparison, trendWindowEndDate)}
      sortValue={rankingSortValue}
      getRowKey={(item) => item.demandIntegrationId}
      searchText={(item) => item.demandIntegrationName}
      pageSize={widget.pageSize}
      loading={loading}
      errorMessage={errorMessage}
      actions={widget.actions}
      reportingSearch={dashboardReportExplorerSearch(widget, range)}
      paginated={false}
      headerControls={
        <>
          <SegmentedControl
            className="h-8"
            ariaLabel={dashboardT('dashboard.srpmRanking.title')}
            value={mode}
            options={srpmRankingOptions}
            onValueChange={setMode}
          />
          <DashboardComparisonToggle checked={showComparison} onCheckedChange={setShowComparison} />
        </>
      }
    />
  );
}

function rankingColumns(showComparison: boolean, trendWindowEndDate: string) {
  return buildDashboardDataTableColumns<DashboardSrpmRankingRow>([
    {
      id: 'rank',
      header: dashboardT('dashboard.table.rank'),
      kind: DashboardDataTableColumnKind.Text,
      value: (row) => row.rank,
      className: 'w-10 text-right tabular-nums',
      headerClassName: 'w-10 text-right',
    },
    {
      id: 'name',
      sortKey: 'integration',
      header: dashboardT('dashboard.table.integration'),
      kind: DashboardDataTableColumnKind.Custom,
      cell: (row) => (
        <DashboardTruncatedName name={row.demandIntegrationName}>
          {row.demandIntegrationId && row.demandPartnerId ? (
            <Link
              className={`${dashboardTruncatedNameClassName} text-primary font-medium hover:underline`}
              to="/demand/integrations/$integrationId/demand/$demandPartnerId/details/overview"
              params={{
                integrationId: row.demandIntegrationId,
                demandPartnerId: row.demandPartnerId,
              }}
            >
              {row.demandIntegrationName}
            </Link>
          ) : (
            <span className={dashboardTruncatedNameClassName}>{row.demandIntegrationName}</span>
          )}
        </DashboardTruncatedName>
      ),
    },
    {
      id: 'srpm',
      header: dashboardT('dashboard.metric.srpm'),
      kind: DashboardDataTableColumnKind.Custom,
      cell: (row) =>
        rankingMetric(formatCurrency(row.srpm), showComparison ? row.srpmChange : undefined),
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
    },
    {
      id: 'trend',
      header: (
        <span
          title={dashboardT('dashboard.srpmRanking.trendTooltip', {
            dateTo: trendWindowEndDate,
          })}
        >
          {dashboardT('dashboard.srpmRanking.trend')}
        </span>
      ),
      kind: DashboardDataTableColumnKind.Custom,
      cell: (row) => rankingTrend(row),
    },
    {
      id: 'bidRequests',
      header: dashboardT('dashboard.table.bidRequests'),
      kind: DashboardDataTableColumnKind.Custom,
      cell: (row) =>
        rankingMetric(
          formatNumber(row.bidRequests),
          showComparison ? row.bidRequestsChange : undefined,
        ),
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
    },
    {
      id: 'revenue',
      header: dashboardT('dashboard.metric.revenue'),
      kind: DashboardDataTableColumnKind.Custom,
      cell: (row) =>
        rankingMetric(formatCurrency(row.revenue), showComparison ? row.revenueChange : undefined),
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
    },
  ]);
}

function rankingTrend(row: DashboardSrpmRankingRow) {
  const values = row.srpmTrend7d.map((point) => point.srpm ?? Number.NaN);
  if (values.filter(Number.isFinite).length < minimumTrendPoints) {
    return (
      <span
        className="text-muted-foreground text-xs"
        title={dashboardT('dashboard.srpmRanking.trendInsufficientData')}
      >
        -
      </span>
    );
  }

  return (
    <Sparkline
      ariaLabel={dashboardT('dashboard.srpmRanking.trendAria', {
        name: row.demandIntegrationName,
      })}
      color="var(--chart-1)"
      height={18}
      values={values}
      width={160}
    />
  );
}

function rankingMetric(formattedValue: string, change: number | null | undefined) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <span>{formattedValue}</span>
      {change === null || change === undefined ? null : rankingChange(change)}
    </div>
  );
}

function rankingChange(value: number) {
  let Icon = Minus;
  if (value > 0) Icon = TrendingUp;
  if (value < 0) Icon = TrendingDown;
  const tone = dashboardTrendTone(value);
  return (
    <span className={`${tone.className} inline-flex items-center gap-1 text-xs tabular-nums`}>
      <Icon className="size-3" aria-hidden="true" />
      {value > 0 ? '+' : ''}
      {formatNumber(value, 2)}%
    </span>
  );
}

function rankingSortValue(row: DashboardSrpmRankingRow, key: string) {
  if (key === 'trend') {
    const values = row.srpmTrend7d
      .map((point) => point.srpm)
      .filter((value): value is number => value !== null && Number.isFinite(value));
    const first = values[0];
    const last = values.at(-1);
    return values.length >= minimumTrendPoints && first !== undefined && last !== undefined
      ? last - first
      : undefined;
  }
  const values: Record<string, string | number | null | undefined> = {
    rank: row.rank,
    integration: row.demandIntegrationName,
    srpm: row.srpm,
    bidRequests: row.bidRequests,
    revenue: row.revenue,
  };
  return values[key];
}
